const MINUTES_STEP = 15;

const pad2 = (value) => String(value).padStart(2, '0');

export const formatLocalDateValue = (date) => (
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
);

export const formatLocalTimeValue = (date) => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

export const composeIsoLocal = (datePart, timePart) => {
  if (!datePart || !timePart) {
    return '';
  }
  return `${datePart}T${timePart}`;
};

export const parseOfferIsoLocal = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return null;
  }

  const normalized = text.includes('T') ? text : text.replace(' ', 'T');
  const [datePart, timePart] = normalized.split('T');
  if (!datePart || !timePart) {
    return null;
  }

  const [year, month, day] = datePart.split('-').map((part) => Number(part));
  const match = timePart.match(/^(\d{1,2}):(\d{1,2})/);
  const hour = match ? Number(match[1]) : Number.NaN;
  const minute = match ? Number(match[2]) : Number.NaN;
  if (!year || !month || !day || Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  return new Date(year, month - 1, day, hour, minute, 0, 0);
};

export const formatOfferDateLabel = (value, options = {}) => {
  const date = parseOfferIsoLocal(value);
  if (!date) {
    return '';
  }

  const {
    includeYear = true,
    includeTime = true
  } = options;

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    ...(includeYear ? { year: 'numeric' } : {}),
    ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {})
  }).format(date);
};

export const roundUpMinutes = (date, stepMinutes = MINUTES_STEP) => {
  const copy = new Date(date.getTime());
  const minutes = copy.getMinutes();
  const next = Math.ceil(minutes / stepMinutes) * stepMinutes;
  copy.setSeconds(0, 0);
  if (next >= 60) {
    copy.setHours(copy.getHours() + 1);
    copy.setMinutes(0);
  } else {
    copy.setMinutes(next);
  }
  return copy;
};

export const addMinutesToDate = (date, minutesToAdd) => (
  new Date(date.getTime() + (Number(minutesToAdd || 0) * 60 * 1000))
);

export const getOfferEffectiveState = (offer, now = new Date()) => {
  const lifecycle = String(offer?.lifecycle || '').trim().toLowerCase();
  const rawStatusLabel = String(offer?.statusLabel || '').trim();
  const fromDate = parseOfferIsoLocal(offer?.validityFrom);
  const untilDate = parseOfferIsoLocal(offer?.validityUntil);
  const nowMs = now.getTime();

  const isExpiredByDate = untilDate ? untilDate.getTime() <= nowMs : false;
  const isExpired = lifecycle === 'expired'
    || rawStatusLabel.toLowerCase().includes('expired')
    || isExpiredByDate;

  if (isExpired) {
    return {
      key: 'expired',
      statusText: 'Expired',
      detailText: ''
    };
  }

  if (lifecycle === 'paused') {
    return {
      key: 'paused',
      statusText: rawStatusLabel || 'Paused by merchant',
      detailText: ''
    };
  }

  const startsInFuture = fromDate ? fromDate.getTime() > nowMs : false;
  if (startsInFuture || (lifecycle === 'scheduled' && !fromDate)) {
    return {
      key: 'scheduled',
      statusText: 'Scheduled for later',
      detailText: fromDate
        ? `Starts ${formatOfferDateLabel(offer?.validityFrom, { includeYear: true, includeTime: false })}`
        : ''
    };
  }

  return {
    key: 'active',
    statusText: 'Running continuously',
    detailText: ''
  };
};

export const getFutureScheduleDefaults = (offer, now = new Date()) => {
  const minimumStart = roundUpMinutes(now, MINUTES_STEP);
  const existingFrom = parseOfferIsoLocal(offer?.validityFrom);
  const existingUntil = parseOfferIsoLocal(offer?.validityUntil);

  const nextStart = existingFrom && existingFrom.getTime() > minimumStart.getTime()
    ? existingFrom
    : minimumStart;
  const minimumEnd = addMinutesToDate(nextStart, MINUTES_STEP);
  const nextEnd = existingUntil && existingUntil.getTime() > minimumEnd.getTime()
    ? existingUntil
    : minimumEnd;

  return {
    fromDate: formatLocalDateValue(nextStart),
    fromTime: formatLocalTimeValue(nextStart),
    untilDate: formatLocalDateValue(nextEnd),
    untilTime: formatLocalTimeValue(nextEnd)
  };
};
